<script lang="ts">
	import type { Currency } from '$lib/components/types';
	import type { PoiFilterId as PoiFilterIdType } from '../../types';
	import { PoiFilterId } from '$houdini';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';
	import { createEventDispatcher } from 'svelte';
	import RangeSlider from '../../../atoms/range-slider/RangeSlider.svelte';

	export let min: number;
	export let max: number;
	export let id: PoiFilterIdType;
	export let defaultValue: number;
	export let name: string;
	export let currency: Currency = 'eur';

	const searchParams = getSearchParamsStore(false);
	const dispatch = createEventDispatcher<{
		change: { id: PoiFilterIdType; value: number | undefined };
	}>();

	function getSingleSliderDefaultValue(): number[] {
		return [$searchParams.filters?.[id] ?? defaultValue] as number[];
	}

	function onSingleSliderChange(value: number[]) {
		dispatch('change', { id, value: value[0] === defaultValue ? undefined : value[0] });
	}

	const valueLabelFnByFilterType: Record<string, (value: number[], max: number) => string> = {
		[PoiFilterId.ACCOMMODATION_RATE]: (value: number[], max: number) => {
			if (value[0] === 0) {
				return m.poi_filters__value_label__none();
			}

			if (value[0] === max) {
				return `${value[0]}`;
			}

			return m.poi_filters__value_label__min_count({ count: value[0] });
		},
		[PoiFilterId.ACCOMMODATION_STARS]: (value: number[], max: number) => {
			if (value[0] === 0) {
				return m.poi_filters__value_label__none();
			}

			if (value[0] === max) {
				return `${value[0]}`;
			}

			return m.poi_filters__value_label__min_count({ count: value[0] });
		},
		[PoiFilterId.OTHER_RESTAURANT_RATE]: (value: number[], max: number) => {
			if (value[0] === 0) {
				return m.poi_filters__value_label__none();
			}

			return m.poi_filters__value_label__min_rate({ minRate: value[0], maxRate: max });
		},
		[PoiFilterId.TOURISM_MICHELIN_DISTINCTION]: (value: number[], max: number) => {
			if (value[0] === 0) {
				return m.poi_filters__value_label__none();
			}

			if (value[0] === max) {
				return m.poi_filters__value_label__stars({ count: max });
			}

			if (value[0] === 1) {
				return m.poi_filters__value_label__min_stars__one({ count: value[0] });
			}

			return m.poi_filters__value_label__min_stars__other({ count: value[0] });
		},
		[PoiFilterId.GAS_STATION_LAST_UPDATE]: (value: number[]) => {
			if (value[0] === 0) {
				return m.poi_filters__value_label__none();
			}

			return m.poi_filters__value_label__last_update({ hours: value[0] });
		},
		[PoiFilterId.POWER]: (value: number[]) => {
			if (value[0] === 0) {
				return m.poi_filters__value_label__none();
			}
			return m.poi_filters__value_label__min_power({ power: value[0] });
		},
	};
</script>

<RangeSlider
	{min}
	{max}
	getValueLabel={valueLabelFnByFilterType[id] ?? (value => `${value[0]}`)}
	defaultValue={getSingleSliderDefaultValue()}
	label={name}
	step={id === PoiFilterId.GAS_STATION_LAST_UPDATE ? 12 : 1}
	labelIcon={id === PoiFilterId.RESTAURANT_MAX_PRICE || id === PoiFilterId.ACCOMMODATION_MAX_PRICE
		? `currency-${currency}`
		: undefined}
	onValueChange={range => onSingleSliderChange(range)}
/>
